import { useState, useRef, useEffect } from "react";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import { RiUser3Fill } from "react-icons/ri";
import Loader from "../../Utility/Loader/Loader";
import { formatDateTime } from "../../utils/dateUtils";
import { formatIsoMonth } from "../../utils/monthLockApi";

const AuditLogPanel = ({
  auditLogs = [],
  auditLoading = false,
  auditError = null,
  total = 0,
  page = 1,
  limit = 20,
  onFetchAuditLogs,
  canViewAudit = true,
  /** When false, audit export control is hidden (requires {@code REPORTS_AUDIT_EXPORT}). */
  canExportAudit = false,
  auditExporting = false,
  exportError = null,
  onExportAudit,
}) => {
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const goPrev = () => {
    if (page <= 1 || !onFetchAuditLogs) return;
    onFetchAuditLogs({ page: page - 1, limit });
  };

  const goNext = () => {
    if (!onFetchAuditLogs) return;
    const lastPage = Math.max(1, Math.ceil(total / limit) || 1);
    if (page >= lastPage) return;
    onFetchAuditLogs({ page: page + 1, limit });
  };

  if (!canViewAudit) {
    return (
      <div className="bg-surface-primary rounded-lg border border-border-primary flex flex-col h-full max-h-full overflow-hidden p-4">
        <Typography variant="h6" className="text-text-primary mb-2">
          Audit Log
        </Typography>
        <Typography variant="text2" className="text-text-secondary">
          You don&apos;t have permission to view the audit log.
        </Typography>
      </div>
    );
  }

  const lastPage = Math.max(1, Math.ceil(total / limit) || 1);
  const canGoPrev = page > 1;
  const canGoNext = page < lastPage && total > 0;

  return (
    <div className="bg-surface-primary rounded-lg border border-border-primary flex flex-col h-full max-h-full overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-primary">
        <Typography variant="h6" className="text-text-primary">
          Audit Log
        </Typography>
        {canExportAudit ? (
          <div className="relative" ref={exportDropdownRef}>
            <Button
              className="btn-reset"
              size="sm"
              type="button"
              disabled={auditExporting}
              onClick={() => {
                if (!auditExporting) setExportDropdownOpen((prev) => !prev);
              }}
            >
              {auditExporting ? "Exporting…" : "Export"}
            </Button>
            {exportDropdownOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <Button
                  type="button"
                  disabled={auditExporting}
                  onClick={() => {
                    setExportDropdownOpen(false);
                    onExportAudit?.("csv");
                  }}
                  className="!w-full !flex !items-center !px-4 !py-2.5 !text-sm !text-gray-700 hover:!bg-gray-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
                >
                  <Typography variant="text2" component="span">
                    Export CSV
                  </Typography>
                </Button>
                <Button
                  type="button"
                  disabled={auditExporting}
                  onClick={() => {
                    setExportDropdownOpen(false);
                    onExportAudit?.("xlsx");
                  }}
                  className="!w-full !flex !items-center !px-4 !py-2.5 !text-sm !text-gray-700 hover:!bg-gray-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
                >
                  <Typography variant="text2" component="span">
                    Export XLSX
                  </Typography>
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {exportError && (
        <div className="px-4 pt-2">
          <Typography variant="caption" className="text-red-500">
            {exportError}
          </Typography>
        </div>
      )}

      {auditError && (
        <div className="px-4 pt-3">
          <Typography variant="caption" className="text-red-500">
            {auditError}
          </Typography>
        </div>
      )}

      <div className="overflow-y-auto px-4 py-3 space-y-3 relative min-h-[120px]">
        {auditLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface-primary/60">
            <Loader />
          </div>
        )}
        {auditLogs.map((log) => (
          <div
            key={log.id}
            className="border border-border-primary rounded-md px-3 py-2 bg-surface-secondary/40"
          >
            <div className="flex items-center justify-between gap-2">
              <Typography variant="caption" className="text-text-secondary">
                {formatDateTime(log.timestamp)}
              </Typography>
            </div>
            <Typography
              variant="text2"
              className="mt-1 text-text-primary font-medium flex items-center gap-1 flex-wrap"
            >
              <RiUser3Fill className="text-[13px] text-text-secondary shrink-0" />
              <Typography variant="span1" component="span">
                {log.user}
              </Typography>

              <Typography variant="span2" component="span">
                {log.action}
              </Typography>

              <Typography variant="span2" component="span">
                {formatIsoMonth(log.month)}
              </Typography>
            </Typography>

            <Typography variant="caption" className="text-text-secondary">
              Status {log.previousStatus} → {log.newStatus}
            </Typography>
            {log.reason && (
              <Typography variant="caption" className="mt-1 text-text-secondary">
                Reason: {log.reason}
              </Typography>
            )}
          </div>
        ))}
        {!auditLoading && auditLogs.length === 0 && (
          <Typography variant="text2" className="text-text-secondary text-center mt-4">
            No audit log entries yet.
          </Typography>
        )}
      </div>

      {total > 0 && (
        <div className="px-4 py-2 border-t border-border-primary flex items-center justify-between gap-2 flex-wrap">
          <Typography variant="caption" className="text-text-secondary">
            Page {page} of {lastPage} · {total} total
          </Typography>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!canGoPrev}
              onClick={goPrev}
            >
              Previous
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!canGoNext}
              onClick={goNext}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogPanel;
